import type {
  AppointmentChecklistDependency,
  AppointmentChecklistItem,
} from "@/types/event";
import type { ServiceChecklistItem } from "@/types/service";

/**
 * The steps still standing between `item` and its checklist actions/display.
 * Completion evidence never bypasses a visual lock: actions taken elsewhere
 * remain recorded and become visible as completed when prerequisites (including
 * their own prerequisites) are done. This never changes recorded completion.
 *
 * Prefers the live row in `items` so an optimistic tick unblocks the next
 * step immediately; falls back to the server snapshot for steps the caller
 * can't see, such as ones hidden from Patient Status.
 */
export function getIncompleteDependencies(
  item: AppointmentChecklistItem,
  items: readonly AppointmentChecklistItem[],
): AppointmentChecklistDependency[] {
  if (!item.dependsOn?.length) return [];
  const byId = new Map(items.map((i) => [i.id, i]));
  const settled = new Map<string, boolean>();
  const visiting = new Set<string>([item.id]);
  const isSettled = (dependency: AppointmentChecklistDependency): boolean => {
    const live = byId.get(dependency.id);
    if (!live) return dependency.completed;
    if (!live.completedDate || visiting.has(live.id)) return false;
    const cached = settled.get(live.id);
    if (cached !== undefined) return cached;
    visiting.add(live.id);
    const complete = (live.dependsOn ?? []).every(isSettled);
    visiting.delete(live.id);
    settled.set(live.id, complete);
    return complete;
  };
  return item.dependsOn.filter((dependency) => !isSettled(dependency));
}

export function formatBlockedByMessage(
  dependencies: readonly AppointmentChecklistDependency[],
): string {
  const names = dependencies.map((d) => d.name);
  const list =
    names.length <= 1
      ? (names[0] ?? "")
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `Complete ${list} first.`;
}

/**
 * Re-point each `dependsOn` index after the list is rearranged.
 *
 * `newOrder[i]` is the old index of the item that now sits at index `i`.
 * Items missing from `newOrder` were removed, and a dependency on one is
 * cleared.
 */
export function remapChecklistDependencies(
  items: readonly ServiceChecklistItem[],
  newOrder: readonly number[],
): ServiceChecklistItem[] {
  const newIndexByOld = new Map(newOrder.map((oldIndex, i) => [oldIndex, i]));
  return newOrder.map((oldIndex) => {
    const item = items[oldIndex];
    if (!item.dependsOn?.length) return item;
    return {
      ...item,
      dependsOn: item.dependsOn
        .map((dep) => newIndexByOld.get(dep))
        .filter((dep): dep is number => dep !== undefined),
    };
  });
}

/** Indexes `index` can't depend on without forming a loop: itself and every
 *  step that already (directly or indirectly) depends on it. */
export function getDependencyCandidatesExcluded(
  items: readonly ServiceChecklistItem[],
  index: number,
): Set<number> {
  const excluded = new Set<number>([index]);
  let grew = true;
  while (grew) {
    grew = false;
    items.forEach((item, i) => {
      if (excluded.has(i)) return;
      if (item.dependsOn?.some((dep) => excluded.has(dep))) {
        excluded.add(i);
        grew = true;
      }
    });
  }
  return excluded;
}
